import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { auth } from '../utils/auth';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Custom hook for Case Workspace data fetching and state management
 */
export const useCaseWorkspace = (caseId) => {
  const navigate = useNavigate();
  
  // Store user in ref to avoid infinite re-renders (auth.getUser() creates new object each time)
  const userRef = useRef(auth.getUser());
  const user = userRef.current;
  
  const [caseData, setCaseData] = useState(null);
  const [timeEntries, setTimeEntries] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Track if we've already fetched to prevent duplicate calls
  const hasFetched = useRef(false);

  const fetchCaseData = useCallback(async () => {
    try {
      const token = auth.getToken();
      const [caseRes, workspaceRes, docsRes] = await Promise.all([
        axios.get(`${API}/cases/${caseId}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/lexy/workspace/${caseId}`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: null })),
        axios.get(`${API}/cases/${caseId}/documents`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { documents: [] } }))
      ]);
      
      setCaseData(caseRes.data);
      setDocuments(docsRes.data.documents || []);
      
      if (workspaceRes.data) {
        setTimeEntries(workspaceRes.data.time?.entries || []);
        setTasks(workspaceRes.data.tasks?.tasks || []);
      }
      
      // Fetch client data if client_id exists
      const clientId = caseRes.data?.client_id;
      if (clientId) {
        try {
          const clientRes = await axios.get(
            `${API}/clients/${clientId}`, 
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setClient(clientRes.data);
        } catch (clientError) {
          console.log('Could not fetch client data:', clientError);
          // Create a minimal client object from case data
          setClient({
            id: clientId,
            name: caseRes.data?.client_name || 'Klient',
            email: caseRes.data?.client_email
          });
        }
      }
    } catch (error) {
      console.error('Error fetching case:', error);
      toast.error('Kunne ikke laste sak');
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  // Reset hasFetched when caseId changes
  useEffect(() => {
    hasFetched.current = false;
  }, [caseId]);

  // Check authentication and fetch data on mount
  useEffect(() => {
    const allowedRoles = ['LAWYER', 'ADMIN', 'TENANT_ADMIN'];
    if (!user || !allowedRoles.includes(user.role)) {
      navigate('/lexy');
      return;
    }
    
    // Only fetch once per caseId change
    if (!hasFetched.current) {
      hasFetched.current = true;
      fetchCaseData();
    }
  }, [caseId, navigate, fetchCaseData, user]);

  return {
    caseData,
    timeEntries,
    tasks,
    documents,
    client,
    loading,
    fetchCaseData,
    user
  };
};

export default useCaseWorkspace;
